import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { CalendarRange } from 'lucide-react';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const fmtDMY = (d) => {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return `${dd}-${MON[Number(m) - 1]}-${y}`;
};
export const mlabel = (m) => `${MON[Number(String(m).slice(5, 7)) - 1]} ${String(m).slice(0, 4)}`;

const PRESETS = [
  { k: 'fy', l: 'This financial year' },
  { k: 'quarter', l: 'A quarter' },
  { k: 'month', l: 'A month' },
  { k: 'custom', l: 'Custom dates' },
];

/** FY / quarter / month / custom-range picker shared by the page and the review sheet. */
export default function PerformancePeriodPicker({ fy, fyOptions = [], months = [], currentQuarter = 1, value, onChange, compact = false }) {
  const [kind, setKind] = useState(value?.kind || 'fy');
  const [quarter, setQuarter] = useState(value?.quarter || String(currentQuarter));
  const [month, setMonth] = useState(value?.month || months.slice(-1)[0] || '');
  const [from, setFrom] = useState(value?.date_from || '');
  const [to, setTo] = useState(value?.date_to || '');
  const [qTouched, setQTouched] = useState(false);

  useEffect(() => {
    if (!qTouched) setQuarter(String(currentQuarter));
  }, [currentQuarter, qTouched]);

  const qLabel = useMemo(() => ({ 1: 'Q1 · Apr–Jun', 2: 'Q2 · Jul–Sep', 3: 'Q3 · Oct–Dec', 4: 'Q4 · Jan–Mar' }), []);

  const cb = useRef(onChange);
  cb.current = onChange;
  const last = useRef('');

  useEffect(() => {
    let payload = null;
    if (kind === 'fy') payload = { kind, fy };
    else if (kind === 'quarter') payload = { kind, fy, quarter };
    else if (kind === 'month' && month) payload = { kind, fy, month };
    else if (kind === 'custom' && from && to) payload = { kind, fy, date_from: from, date_to: to };
    if (!payload) return;
    const sig = JSON.stringify(payload);
    if (sig === last.current) return;
    last.current = sig;
    cb.current?.(payload);
  }, [kind, quarter, month, from, to, fy]);

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="perf-period-picker">
      <Select value={fy} onValueChange={(v) => onChange?.({ kind, fy: v, quarter, month: '', date_from: from, date_to: to, fyChanged: true })}>
        <SelectTrigger className={`${compact ? 'h-9 w-36' : 'h-10 w-40'} bg-white text-[13px]`} data-testid="perf-fy"><SelectValue placeholder="FY" /></SelectTrigger>
        <SelectContent className="bg-white">
          {fyOptions.map((o) => <SelectItem key={o} value={o}>FY {o}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={kind} onValueChange={setKind}>
        <SelectTrigger className={`${compact ? 'h-9 w-40' : 'h-10 w-44'} bg-white text-[13px]`} data-testid="perf-period-kind">
          <CalendarRange className="w-3.5 h-3.5 mr-1.5 text-slate-400" /><SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white">
          {PRESETS.map((o) => <SelectItem key={o.k} value={o.k}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>

      {kind === 'quarter' && (
        <Select value={quarter} onValueChange={(v) => { setQTouched(true); setQuarter(v); }}>
          <SelectTrigger className={`${compact ? 'h-9 w-40' : 'h-10 w-44'} bg-white text-[13px]`} data-testid="perf-quarter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>{qLabel[q]}{q === currentQuarter ? ' · current' : ''}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {kind === 'month' && (
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className={`${compact ? 'h-9 w-36' : 'h-10 w-40'} bg-white text-[13px]`} data-testid="perf-month"><SelectValue placeholder="Pick a month" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            {months.map((m) => <SelectItem key={m} value={m}>{mlabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {kind === 'custom' && (
        <span className="flex items-center gap-1.5">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${compact ? 'h-9' : 'h-10'} w-36 bg-white text-[12.5px]`} data-testid="perf-from" />
          <span className="text-[12px] text-slate-400">to</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${compact ? 'h-9' : 'h-10'} w-36 bg-white text-[12.5px]`} data-testid="perf-to" />
        </span>
      )}
    </div>
  );
}
